import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import {
  Button,
  CheckIcon,
  CloseIcon,
  IconButton,
  PencilIcon,
  Textarea,
  Tooltip,
  TooltipProvider
} from '@viniciomendesr/rayrovisk-design-system';
import { CopyToClipboard, type CopySurface } from './CopyToClipboard';
import { PendingEditsGuard } from './PendingEditsGuard';
import { PromptBody, type PromptMarkerRenderProps } from './PromptBody';
import { usePromptDraft } from './usePromptDraft';

export interface PromptEditorProps {
  identity: string;
  template: string;
  surface: CopySurface;
  sourceRef?: string;
  actionBeforeCopy?: ReactNode;
}

export function PromptEditor(props: PromptEditorProps): JSX.Element {
  const draft = usePromptDraft({ identity: props.identity, template: props.template });
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (draft.active) {
      field.current?.focus();
      field.current?.select();
    }
  }, [draft.active]);

  function editFirst(): void {
    const target = draft.markers.find((marker) => !draft.values[marker.name]) ?? draft.markers[0];
    if (target) draft.begin(target.name, 0);
  }

  function renderMarker({ marker, occurrence }: PromptMarkerRenderProps): JSX.Element {
    const label = marker.slice(1, -1);
    const active = draft.active?.marker === marker && draft.active.occurrence === occurrence;
    if (active) {
      return (
        <span className="rvk-prompt-placeholder-editor">
          <Textarea
            ref={field}
            aria-label={label}
            size="sm"
            rows={1}
            autoGrow
            value={draft.draft}
            onChange={(event) => draft.updateDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                draft.cancel();
              } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                draft.confirm();
              }
            }}
          />
          <TooltipProvider>
            <Tooltip content="Confirmar">
              <IconButton
                label={`Confirmar ${label}`}
                icon={<CheckIcon />}
                variant="solid"
                size="sm"
                onClick={draft.confirm}
              />
            </Tooltip>
            <Tooltip content="Cancelar">
              <IconButton
                label={`Cancelar ${label}`}
                icon={<CloseIcon />}
                variant="outline"
                size="sm"
                onClick={draft.cancel}
              />
            </Tooltip>
          </TooltipProvider>
        </span>
      );
    }
    return (
      <button
        type="button"
        className="rvk-prompt-placeholder"
        aria-label={`Editar ${label}`}
        onClick={() => draft.begin(marker, occurrence)}
      >
        <PencilIcon size={14} />
        <span>{draft.values[marker] || marker}</span>
      </button>
    );
  }

  const total = draft.markers.length;
  const progress = `${draft.filledCount} de ${total} ${total === 1 ? 'lacuna preenchida' : 'lacunas preenchidas'}`;

  return (
    <div className="rvk-prompt-editor">
      <PendingEditsGuard when={draft.pending} />
      <div className="rvk-prompt-editor-toolbar">
        <div className="rvk-prompt-editor-meta">
          <span
            style={{
              font: '600 10px/1 var(--rvk-font-functional)',
              letterSpacing: '.13em',
              textTransform: 'uppercase',
              color: 'var(--rvk-text-tertiary)'
            }}
          >
            Prompt
          </span>
          {total > 0 && (
            <span className="rvk-prompt-editor-progress" role="status">
              {progress}
            </span>
          )}
        </div>
        <div className="rvk-prompt-editor-actions">
          {total > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                access={draft.filledCount === 0 && draft.draft === '' ? 'disabled' : 'editable'}
                onClick={draft.clear}
              >
                Limpar
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconStart={<PencilIcon size={14} />}
                onClick={editFirst}
              >
                Editar
              </Button>
            </>
          )}
          {props.actionBeforeCopy}
          <div className="rvk-prompt-editor-copy">
            <CopyToClipboard
              text={draft.text}
              surface={props.surface}
              sourceRef={props.sourceRef}
              confirmation="in-button"
              prepareCopy={draft.prepareCopy}
              onCopySuccess={draft.markCopied}
            />
          </div>
        </div>
      </div>
      <PromptBody text={props.template} renderMarker={total > 0 ? renderMarker : undefined} />
    </div>
  );
}
